// Form abandonment tracking utility
export interface FormTrackingData {
  formId: string;
  formLocation: string;
  fieldsInteracted: string[];
  fieldsCompleted: string[];
  timeOnForm: number;
  progressPercentage: number;
  lastInteraction: number;
  utmData: Record<string, unknown>;
}

class FormTracker {
  private forms: Map<string, FormTrackingData> = new Map();
  private timers: Map<string, NodeJS.Timeout> = new Map();
  private readonly ABANDONMENT_DELAY = 30000; // 30 seconds
  private readonly PROGRESS_THRESHOLD = 25; // 25% completion threshold

  // Initialize tracking for a form
  initForm(formId: string, formLocation: string, totalFields: number, utmData: Record<string, unknown>) {
    const formData: FormTrackingData = {
      formId,
      formLocation,
      fieldsInteracted: [],
      fieldsCompleted: [],
      timeOnForm: 0,
      progressPercentage: 0,
      lastInteraction: Date.now(),
      utmData
    };

    this.forms.set(formId, formData);
    this.startAbandonmentTimer(formId);

    // Track form start
    this.pushEvent('form_start', {
      form_id: formId,
      form_location: formLocation,
      total_fields: totalFields,
      ...utmData
    });
  }

  // Track field interaction
  trackFieldInteraction(formId: string, fieldName: string, fieldType: string = 'text') {
    const formData = this.forms.get(formId);
    if (!formData) return;

    // Update interaction data
    if (!formData.fieldsInteracted.includes(fieldName)) {
      formData.fieldsInteracted.push(fieldName);
    }

    formData.lastInteraction = Date.now();
    this.resetAbandonmentTimer(formId);

    // Track field focus
    // Track field focus - DISABLED to reduce bloat
    /* this.pushEvent('form_field_focus', {
      form_id: formId,
      form_location: formData.formLocation,
      field_name: fieldName,
      field_type: fieldType,
      fields_interacted: formData.fieldsInteracted.length,
      ...formData.utmData
    }); */
  }

  // Track field completion
  trackFieldCompletion(formId: string, fieldName: string, fieldValue: string) {
    const formData = this.forms.get(formId);
    if (!formData) return;

    // Update completion data
    if (!formData.fieldsCompleted.includes(fieldName)) {
      formData.fieldsCompleted.push(fieldName);
    }

    // Calculate progress
    const totalFields = this.getTotalFields(formId);
    formData.progressPercentage = Math.round((formData.fieldsCompleted.length / totalFields) * 100);

    formData.lastInteraction = Date.now();
    this.resetAbandonmentTimer(formId);

    // Track field completion
    // Track field completion - DISABLED to reduce bloat
    /* this.pushEvent('form_field_complete', {
      form_id: formId,
      form_location: formData.formLocation,
      field_name: fieldName,
      field_value_length: fieldValue.length,
      progress_percentage: formData.progressPercentage,
      fields_completed: formData.fieldsCompleted.length,
      ...formData.utmData
    }); */

    // Track progress milestones
    if (formData.progressPercentage >= 25 && formData.progressPercentage < 50) {
      this.pushEvent('form_progress_25', {
        form_id: formId,
        form_location: formData.formLocation,
        progress_percentage: formData.progressPercentage,
        ...formData.utmData
      });
    } else if (formData.progressPercentage >= 50 && formData.progressPercentage < 75) {
      this.pushEvent('form_progress_50', {
        form_id: formId,
        form_location: formData.formLocation,
        progress_percentage: formData.progressPercentage,
        ...formData.utmData
      });
    } else if (formData.progressPercentage >= 75 && formData.progressPercentage < 100) {
      this.pushEvent('form_progress_75', {
        form_id: formId,
        form_location: formData.formLocation,
        progress_percentage: formData.progressPercentage,
        ...formData.utmData
      });
    }
  }

  // Track form submission
  trackFormSubmit(formId: string, success: boolean = true) {
    const formData = this.forms.get(formId);
    if (!formData) return;

    // Calculate final metrics
    const timeOnForm = Date.now() - (formData.lastInteraction - formData.timeOnForm);
    formData.timeOnForm = timeOnForm;

    // Clear abandonment timer
    this.clearAbandonmentTimer(formId);

    // Track submission
    this.pushEvent('form_submit', {
      form_id: formId,
      form_location: formData.formLocation,
      success: success,
      time_on_form: timeOnForm,
      progress_percentage: formData.progressPercentage,
      fields_interacted: formData.fieldsInteracted.length,
      fields_completed: formData.fieldsCompleted.length,
      ...formData.utmData
    });

    // Clean up
    this.forms.delete(formId);
  }

  // Track form abandonment
  private trackFormAbandonment(formId: string) {
    const formData = this.forms.get(formId);
    if (!formData) return;

    // Only track abandonment if user actually interacted with the form
    if (formData.fieldsInteracted.length === 0) {
      this.forms.delete(formId);
      return;
    }

    // Calculate final metrics
    const timeOnForm = Date.now() - (formData.lastInteraction - formData.timeOnForm);
    formData.timeOnForm = timeOnForm;

    // Track abandonment
    this.pushEvent('form_abandonment', {
      form_id: formId,
      form_location: formData.formLocation,
      time_on_form: timeOnForm,
      progress_percentage: formData.progressPercentage,
      fields_interacted: formData.fieldsInteracted.length,
      fields_completed: formData.fieldsCompleted.length,
      last_field_interacted: formData.fieldsInteracted[formData.fieldsInteracted.length - 1] || null,
      abandonment_reason: this.getAbandonmentReason(formData),
      ...formData.utmData
    });

    // Clean up
    this.forms.delete(formId);
  }

  // Get abandonment reason
  private getAbandonmentReason(formData: FormTrackingData): string {
    if (formData.progressPercentage === 0) return 'no_progress';
    if (formData.progressPercentage < 25) return 'early_abandonment';
    if (formData.progressPercentage < 50) return 'mid_abandonment';
    if (formData.progressPercentage < 75) return 'late_abandonment';
    return 'near_completion';
  }

  // Get total fields for a form (you'll need to implement this based on your forms)
  private getTotalFields(formId: string): number {
    // Default field counts for known forms
    const fieldCounts: Record<string, number> = {
      'contact_form': 7, // first_name, last_name, email, phone, company, interested_solution, message
      'trial_signup_form': 3, // name, email, phone
      'newsletter_signup': 1, // email
      'ebook_download': 3, // name, email, phone
    };

    return fieldCounts[formId] || 5; // Default to 5 if unknown
  }

  // Timer management
  private startAbandonmentTimer(formId: string) {
    this.clearAbandonmentTimer(formId);
    const timer = setTimeout(() => {
      this.trackFormAbandonment(formId);
    }, this.ABANDONMENT_DELAY);
    this.timers.set(formId, timer);
  }

  private resetAbandonmentTimer(formId: string) {
    this.startAbandonmentTimer(formId);
  }

  private clearAbandonmentTimer(formId: string) {
    const timer = this.timers.get(formId);
    if (timer) {
      clearTimeout(timer);
      this.timers.delete(formId);
    }
  }

  // Send the event to GA4 via gtag (GTM dropped: GA4-direct is the single path).
  private pushEvent(eventName: string, eventData: Record<string, unknown>) {
    if (typeof window === 'undefined') return;
    const gtag = (window as unknown as { gtag?: (...a: unknown[]) => void }).gtag;
    if (typeof gtag === 'function') gtag('event', eventName, eventData);
  }

  // Clean up all timers (call on page unload)
  cleanup() {
    this.timers.forEach(timer => clearTimeout(timer));
    this.timers.clear();
    this.forms.clear();
  }
}

// Export singleton instance
export const formTracker = new FormTracker();

// Clean up on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    formTracker.cleanup();
  });
}
